import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { Row, Col, Modal, Button } from 'antd'
import { useTranslation } from 'next-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { register, clearError } from '../apis/authSlice'
import { queryCountries } from '../../../redux/countriesSlice'
import { errorHandling, openNotification } from '../../../utils/utils'
import { getPostAuthRedirectPath } from '../../../utils/authRedirect'

import Close from '../../../components/svgs/CloseModal'
import InputAffix from '../../../components/shared-components/form/InputAffix'
import PasswordInput from '../../../components/shared-components/form/PasswordInput'
import SocialButtons from '../../../components/shared-components/SocialButtons'
import SelectCountryCodes from '../../../components/shared-components/form/SelectCountryCodes'

import Email from '../../../components/svgs/auth/Email'
import Human from '../../../components/svgs/auth/Human'

const Register = ({ open, setOpen, onDismiss }) => {
  const { t } = useTranslation('HomeLayout')
  const router = useRouter()
  const dispatch = useDispatch()

  const { loading, errors } = useSelector((state) => state.auth)

  const handleDismiss = () => {
    localStorage.removeItem('SMARTCAF_SERVICE_ID')
    setOpen(false)
    dispatch(clearError())
    onDismiss?.()
  }

  const [formValues, setFormValues] = useState({
    name: '',
    lname: '',
    email: '',
    password: '',
    password_confirmation: '',
    mobile_number_code: '+39',
    mobile_number: '',
  })

  useEffect(() => {
    if (!open) {
      return
    }

    dispatch(queryCountries())
  }, [dispatch, open])

  // SUBMITING FORM
  const handleSubmit = (event) => {
    event?.preventDefault()

    let formData = new FormData()

    formData.set('lang', 'it')
    for (var key in formValues) {
      formData.append(key, formValues[key])
    }

    dispatch(register(formData))
      .unwrap()
      .then(() => {
        setOpen(false)
        const nextRedirectPath = getPostAuthRedirectPath(router.query['redirection_link'])

        if (localStorage.getItem('redirection_link')) {
          localStorage.removeItem('redirection_link')
        }

        if (nextRedirectPath) {
          router.push(nextRedirectPath)
        } else if (localStorage.getItem('SMARTCAF_conversation_token')) {
          router.push('/dashboard/tickets')
        } else if (localStorage.getItem('SMARTCAF_SERVICE_ID')) {
          router.push(
            `/dashboard/servizi/${localStorage.getItem(
              'SMARTCAF_SERVICE_ID'
            )}/request`
          )
          localStorage.removeItem('SMARTCAF_SERVICE_ID')
        } else {
          router.push('/dashboard/servizi')
        }
      })
      .catch((error) => {
        const message = errorHandling(error)
        openNotification('error', message)
      })
  }

  return (
    <Modal
      className='!w-[50rem]'
      title={null}
      open={open}
      onCancel={handleDismiss}
      footer={null}
      closable={false}
      centered
    >
      <div className='text-center mb-24 mt-16'>
        <h3 className='text-xl font-semibold capitalize mb-8'>
          {t('Crea il tuo account personale ')}
        </h3>
        <p className='text-dimmed leading-6 text-sm w-9/12 mx-auto'>
          {t('Benvenuto in SmartCaf')}
        </p>
      </div>

      <SocialButtons />

      <form className='form-type_5' onSubmit={handleSubmit}>
        <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <InputAffix
            label={`${t('First Name')}`}
            placeholder={t('Scrivi il tuo Nome')}
            name='name'
            value={formValues.name}
            onChange={(e) =>
              setFormValues({ ...formValues, name: e.target.value })
            }
            error={errors?.name}
            icon={<Human style='h-20' />}
          />
        </Col>
        <Col xs={24} sm={12}>
          <InputAffix
            label={`${t('Last Name')}`}
            placeholder={t('Scrivi il tuo Cognome')}
            name='lname'
            value={formValues.lname}
            onChange={(e) =>
              setFormValues({ ...formValues, lname: e.target.value })
            }
            error={errors?.lname}
            icon={<Human style='h-20' />}
          />
        </Col>
        <Col xs={24} sm={12}>
          <InputAffix
            label={`${t('form.email.label')}`}
            placeholder={t('form.email.ph')}
            name='email'
            autoComplete='email'
            value={formValues.email}
            onChange={(e) =>
              setFormValues({ ...formValues, email: e.target.value })
            }
            error={errors?.email}
            icon={<Email style='h-20' />}
          />
        </Col>
        <Col xs={24} sm={12}>
          <div className={`input ${errors?.mobile_number ? 'error' : ''}`}>
            <label htmlFor='mobile_number'>
              {t('form.mobile.label', { defaultValue: 'Numero di cellulare' })}
            </label>
            <div
              className={`phone-number-field ${
                errors?.mobile_number ? 'error' : ''
              }`}
            >
              <div className='phone-number-field__code'>
                <SelectCountryCodes
                  className='!bg-transparent'
                  value={formValues.mobile_number_code}
                  setValue={(value) => {
                    setFormValues({
                      ...formValues,
                      mobile_number_code: `+${value}`,
                    })
                  }}
                />
              </div>
              <input
                id='mobile_number'
                className='ant-input min-w-0 flex-1'
                name='mobile_number'
                type='text'
                value={formValues.mobile_number}
                onChange={(e) =>
                  setFormValues({
                    ...formValues,
                    mobile_number: e.target.value,
                  })
                }
                placeholder={t('form.mobile.ph', {
                  defaultValue: 'Inserisci il numero',
                })}
                autoComplete='tel-national'
              />
            </div>
            <small className='text-danger text-sm'>{errors?.mobile_number}</small>
          </div>
        </Col>
        <Col xs={24} sm={12}>
          <PasswordInput
            label={`${t('form.password.label')}`}
            placeholder={t('form.password.ph')}
            name='password'
            autoComplete='new-password'
            value={formValues.password}
            onChange={(e) =>
              setFormValues({
                ...formValues,
                password: e.target.value,
                password_confirmation: e.target.value,
              })
            }
            error={errors?.password}
          />
        </Col>
      </Row>

        <Button
          htmlType='submit'
          className='block btn-green text-base font-semibold tracking-wide rounded-lg py-12 h-auto mt-32 mb-20 w-7/12 mx-auto'
          loading={loading}
        >
          {t('register.submit')}
        </Button>
      </form>

      <Close
        style='absolute top-28 right-36 cursor-pointer'
        onClick={handleDismiss}
      />
    </Modal>
  )
}

export default Register
